import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, FileText, Download, Eye, User, Calendar, TrendingUp, DollarSign, BarChart3, PieChart } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { apiService } from '../../services/apiService'
import { generateFinancialStatementPDF } from '../../utils/pdfGenerator'
import AnalyticsDashboard from '../../components/AnalyticsDashboard'

const AdminFinancialStatements = () => {
  const { admin } = useAdminAuth()
  const [statements, setStatements] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedStatement, setSelectedStatement] = useState(null)
  const [viewMode, setViewMode] = useState('list') // 'list', 'detail', 'analytics'
  const [filter, setFilter] = useState('all') // 'all', 'profit_loss', 'balance_sheet'
  const [downloadingPDF, setDownloadingPDF] = useState(false)

  useEffect(() => {
    fetchFinancialStatements()
  }, [])

  const normalizeStatement = (s) => ({
    id: s.id,
    statement_type: s.statementType || s.statement_type,
    customer_name: s.customerName || s.customer_name,
    customer_id: s.customerId || s.customer_id,
    company_id: s.companyId || s.company_id,
    company_name: s.companyName || s.company_name,
    reporting_period: s.reporting_period || s.reportingPeriod || '',
    financial_data: s.data || s.financial_data || {},
    created_at: s.createdAt || s.created_at,
    member_number: s.memberNumber || s.member_number,
  })

  const fetchFinancialStatements = async () => {
    try {
      setLoading(true)
      const response = await apiService.getFinancialStatements()
      if (response.success || response.status === 'SUCCESS') {
        const rawStatements = response.data || []
        setStatements(rawStatements.map(normalizeStatement))
      } else {
        console.error('Failed to fetch financial statements:', response.message)
      }
    } catch (error) {
      console.error('Failed to fetch financial statements:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: 'UGX',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      }).format(amount / 1000000) + 'M'
    } else if (amount >= 1000) {
      return new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: 'UGX',
        minimumFractionDigits: 0
      }).format(amount / 1000) + 'K'
    }
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const getStatementTypeLabel = (type) => {
    return type === 'profit_loss' ? 'Profit & Loss' : 'Balance Sheet'
  }

  const getStatementTypeColor = (type) => {
    return type === 'profit_loss' ? 'text-success-600 bg-success-100' : 'text-primary-600 bg-primary-100'
  }

  const viewStatementDetail = (statement) => {
    setSelectedStatement(statement)
    setViewMode('detail')
  }

  const downloadStatementPDF = async (statement) => {
    try {
      setDownloadingPDF(true)
      
      const pdfData = {
        customerName: statement.customer_name,
        memberId: statement.member_number,
        statementType: statement.statement_type,
        reportingPeriod: statement.reporting_period,
        createdAt: statement.created_at,
        financialData: statement.financial_data,
        companyName: statement.company_name || 'UFCC Microfinance'
      }

      const result = await generateFinancialStatementPDF(pdfData)
      
      if (result.success) {
        // Show success message or notification
        console.log('PDF downloaded successfully:', result.filename)
      } else {
        console.error('PDF generation failed:', result.error)
        alert('Failed to generate PDF: ' + result.error)
      }
    } catch (error) {
      console.error('PDF download failed:', error)
      alert('Failed to download PDF: ' + error.message)
    } finally {
      setDownloadingPDF(false)
    }
  }

  const filteredStatements = statements.filter(statement => 
    filter === 'all' || statement.statement_type === filter
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (viewMode === 'detail' && selectedStatement) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => setViewMode('list')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Statements
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {getStatementTypeLabel(selectedStatement.statement_type)}
                </h1>
                <p className="text-gray-600 mt-2">
                  Customer: {selectedStatement.customer_name} | Period: {selectedStatement.reporting_period}
                </p>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatementTypeColor(selectedStatement.statement_type)}`}>
                {getStatementTypeLabel(selectedStatement.statement_type)}
              </span>
            </div>
          </div>

          {/* Statement Details */}
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Financial Data</h3>
                </div>
                <div className="card-body">
                  {selectedStatement.financial_data && Object.keys(selectedStatement.financial_data).length > 0 ? (
                    <div className="space-y-6">
                      {/* Regular financial data (excluding products and operating_expenses) */}
                      {Object.entries(selectedStatement.financial_data)
                        .filter(([key]) => key !== 'products' && key !== 'operating_expenses')
                        .map(([category, items]) => (
                        <div key={category} className="border border-gray-200 rounded-lg p-4">
                          <h4 className="text-lg font-medium text-gray-900 mb-4 capitalize">
                            {category.replace(/_/g, ' ')}
                          </h4>
                          <div className="space-y-2">
                            {typeof items === 'object' && items !== null ? (
                              Object.entries(items).map(([item, value]) => (
                                <div key={item} className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                                  <span className="text-gray-600 capitalize">{item.replace(/_/g, ' ')}</span>
                                  <span className="font-medium text-gray-900">
                                    {typeof value === 'number' ? formatCurrency(value) : value}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="flex justify-between py-2">
                                <span className="text-gray-600">Value</span>
                                <span className="font-medium text-gray-900">
                                  {typeof items === 'number' ? formatCurrency(items) : items}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Products Section */}
                      {(selectedStatement.products || selectedStatement.financial_data?.products) && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h4 className="text-lg font-medium text-gray-900 mb-4">Products</h4>
                          <div className="space-y-4">
                            {(selectedStatement.products || selectedStatement.financial_data.products).map((product, index) => (
                              <div key={index} className="bg-gray-50 rounded-lg p-4">
                                <h5 className="font-medium text-gray-900 mb-2">{product.product_name}</h5>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Revenue:</span>
                                    <span className="font-medium">{formatCurrency(product.revenue)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Cost of Goods Sold:</span>
                                    <span className="font-medium">{formatCurrency(product.cost_of_goods_sold)}</span>
                                  </div>
                                  <div className="flex justify-between col-span-2 border-t pt-2">
                                    <span className="text-gray-600 font-medium">Gross Profit:</span>
                                    <span className="font-medium text-success-600">{formatCurrency(product.gross_profit)}</span>
                                  </div>
                                  {product.notes && (
                                    <div className="col-span-2 border-t pt-2">
                                      <span className="text-gray-600">Notes:</span>
                                      <p className="text-sm text-gray-700 mt-1">{product.notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Operating Expenses Section */}
                      {(selectedStatement.operating_expenses || selectedStatement.financial_data?.operating_expenses) && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h4 className="text-lg font-medium text-gray-900 mb-4">Operating Expenses</h4>
                          <div className="space-y-3">
                            {(selectedStatement.operating_expenses || selectedStatement.financial_data.operating_expenses).map((expense, index) => (
                              <div key={index} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
                                <div className="flex-1">
                                  <span className="font-medium text-gray-900">{expense.category || expense.expense_category}</span>
                                  {(expense.description || expense.expense_description) && (
                                    <p className="text-sm text-gray-600 mt-1">{expense.description || expense.expense_description}</p>
                                  )}
                                </div>
                                <span className="font-medium text-gray-900 ml-4">{formatCurrency(expense.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600">No financial data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Panel */}
            <div className="lg:col-span-1">
              <div className="card mb-6">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Statement Info</h3>
                </div>
                <div className="card-body space-y-4">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Customer</p>
                      <p className="font-medium text-gray-900">{selectedStatement.customer_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Period</p>
                      <p className="font-medium text-gray-900">{selectedStatement.reporting_period}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Type</p>
                      <p className="font-medium text-gray-900">{getStatementTypeLabel(selectedStatement.statement_type)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Submitted</p>
                      <p className="font-medium text-gray-900">
                        {new Date(selectedStatement.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Actions</h3>
                </div>
                <div className="card-body space-y-3">
                  <button 
                    onClick={() => downloadStatementPDF(selectedStatement)}
                    disabled={downloadingPDF}
                    className="w-full btn btn-primary disabled:opacity-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {downloadingPDF ? 'Generating PDF...' : 'Download PDF'}
                  </button>
                  <button 
                    onClick={() => setViewMode('analytics')}
                    className="w-full btn btn-secondary"
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    View Analytics
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Analytics View
  if (viewMode === 'analytics') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => setViewMode('list')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Statements
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Financial Analytics
                </h1>
                <p className="text-gray-600 mt-2">
                  Analytics dashboard for {statements.length} financial statements
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <PieChart className="h-8 w-8 text-primary-600" />
                <span className="text-2xl font-bold text-primary-600">{statements.length}</span>
              </div>
            </div>
          </div>

          {/* Analytics Dashboard */}
          <AnalyticsDashboard financialStatements={statements} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Link to="/admin/dashboard" className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Financial Statements</h1>
              <p className="text-gray-600 mt-2">
                Review customer financial statements | MG: {admin?.mgCode}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-white rounded-lg p-1 border">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-primary-600 text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FileText className="h-4 w-4 mr-2 inline" />
                  List
                </button>
                <button
                  onClick={() => setViewMode('analytics')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'analytics' 
                      ? 'bg-primary-600 text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <BarChart3 className="h-4 w-4 mr-2 inline" />
                  Analytics
                </button>
              </div>
              <select 
                value={filter} 
                onChange={(e) => setFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Statements</option>
                <option value="profit_loss">Profit & Loss</option>
                <option value="balance_sheet">Balance Sheet</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Statements</p>
                <p className="text-2xl font-bold text-gray-900">{statements.length}</p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <TrendingUp className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Profit & Loss</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statements.filter(s => s.statement_type === 'profit_loss').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-info-100">
                <DollarSign className="h-6 w-6 text-info-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Balance Sheets</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statements.filter(s => s.statement_type === 'balance_sheet').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Statements List */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Submitted Statements</h3>
          </div>
          <div className="card-body">
            {filteredStatements.length > 0 ? (
              <div className="space-y-4">
                {filteredStatements.map((statement) => (
                  <div key={statement.id} className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <h4 className="text-lg font-medium text-gray-900">
                            {statement.customer_name}
                          </h4>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatementTypeColor(statement.statement_type)}`}>
                            {getStatementTypeLabel(statement.statement_type)}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Period:</span> {statement.reporting_period}
                          </div>
                          <div>
                            <span className="font-medium">Submitted:</span> {new Date(statement.created_at).toLocaleDateString()}
                          </div>
                          <div>
                            <span className="font-medium">Customer ID:</span> {statement.customer_id}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => viewStatementDetail(statement)}
                          className="btn btn-primary btn-sm"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </button>
                        <button 
                          onClick={() => downloadStatementPDF(statement)}
                          disabled={downloadingPDF}
                          className="btn btn-secondary btn-sm disabled:opacity-50"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {downloadingPDF ? 'Exporting...' : 'Export PDF'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Financial Statements</h3>
                <p className="text-gray-600">
                  {filter === 'all' 
                    ? 'No financial statements have been submitted yet.'
                    : `No ${filter.replace('_', ' ')} statements found.`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminFinancialStatements
